import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getInviteByToken, acceptInvite } from '../../services/groupService';

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getInviteByToken(token);
        setInvite(data.invite);
      } catch (err) {
        setError(err.response?.data?.message || 'This invite link is invalid');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    setError('');
    try {
      await acceptInvite(token);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return <div style={styles.container}><p style={styles.text}>Loading invite...</p></div>;
  }

  if (error && !invite) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.title}>This Invite No Longer Works</h2>
          <p style={styles.text}>{error}</p>
          <p style={styles.warning}>
            Logging in will <strong>not</strong> add you to the cookbook this invite was for —
            invite links only work once and can expire. Ask whoever invited you to send a fresh one.
          </p>
          <Link to="/login" style={styles.link}>Go to login (for a different reason)</Link>
        </div>
      </div>
    );
  }

  const emailMatches = isAuthenticated && user?.email?.toLowerCase() === invite?.email?.toLowerCase();

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>🍽️ You've been invited to "{invite.groupName}"</h2>
        <p style={styles.text}>This invite was sent to <strong>{invite.email}</strong>.</p>

        {!isAuthenticated && (
          <div style={styles.actions}>
            <p style={styles.text}>
              {invite.userExists
                ? "Log in with this email, and you'll come right back here to accept."
                : "Create an account with this email, and you'll come right back here to accept."}
            </p>
            <Link
              to={`${invite.userExists ? '/login' : '/register'}?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(invite.email)}`}
              style={styles.button}
            >
              {invite.userExists ? 'Log In' : 'Create Account'}
            </Link>
          </div>
        )}

        {isAuthenticated && !emailMatches && (
          <p style={styles.errorMsg}>
            You're logged in as {user.email}, but this invite is for {invite.email}.
            Please log out and log in with the correct account.
          </p>
        )}

        {isAuthenticated && emailMatches && (
          <div style={styles.actions}>
            <button onClick={handleAccept} style={styles.button} disabled={accepting}>
              {accepting ? 'Joining...' : 'Accept & Join'}
            </button>
            {error && <p style={styles.errorMsg}>{error}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    textAlign: 'center',
    maxWidth: '460px',
  },
  title: {
    color: '#e67e22',
    fontSize: '22px',
    marginBottom: '12px',
  },
  text: {
    color: '#666',
    fontSize: '15px',
    lineHeight: 1.5,
  },
  warning: {
    color: '#a05a1a',
    backgroundColor: '#fdf0e3',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    lineHeight: 1.5,
    marginTop: '14px',
    marginBottom: '14px',
  },
  actions: {
    marginTop: '20px',
  },
  button: {
    display: 'inline-block',
    padding: '12px 28px',
    backgroundColor: '#e67e22',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  errorMsg: {
    color: '#e74c3c',
    fontSize: '14px',
    marginTop: '14px',
  },
  link: {
    color: '#e67e22',
    fontWeight: '600',
  },
};

export default AcceptInvite;
